import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";

// Campo di ricerca testuale lato server: carica i risultati solo mentre si digita (min 2 caratteri).
// value = id selezionato; onChange(obj|null); cerca(q) -> lista; risolvi(id) -> obj (per mostrare l'etichetta di un valore già salvato).
export function RicercaAsync({ value, onChange, onRisolto, cerca, risolvi, label, placeholder = "Cerca…", testid, minChars = 2, className = "", disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [ris, setRis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const box = useRef(null);
  const lista = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return;
    const aggiorna = () => { const r = box.current?.getBoundingClientRect(); if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) }); };
    aggiorna();
    window.addEventListener("scroll", aggiorna, true); window.addEventListener("resize", aggiorna);
    return () => { window.removeEventListener("scroll", aggiorna, true); window.removeEventListener("resize", aggiorna); };
  }, [open]);

  useEffect(() => {
    if (!value) { setSel(null); return; }
    if (sel && sel.id === value) return;
    let vivo = true;
    risolvi(value).then((o) => { if (vivo) { setSel(o); if (o) { setOpen(false); onRisolto?.(o); } } }).catch(() => {});
    return () => { vivo = false; };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const testo = q.trim();
    if (testo.length < minChars) { setRis([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { setErr(""); setRis(await cerca(testo)); } catch (e) { setRis([]); setErr(e?.response?.data?.detail || "Errore nel caricamento dell'elenco"); } finally { setLoading(false); }
    }, minChars === 0 && !testo ? 0 : 250);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fuori = (e) => { if (box.current && !box.current.contains(e.target) && !(lista.current && lista.current.contains(e.target))) setOpen(false); };
    document.addEventListener("mousedown", fuori);
    return () => document.removeEventListener("mousedown", fuori);
  }, []);

  const scegli = (o) => { setSel(o); onChange(o); setOpen(false); setQ(""); };
  const pulisci = (e) => { e.stopPropagation(); setSel(null); onChange(null); setQ(""); setOpen(true); };

  return (
    <div ref={box} className={`relative ${className}`} data-testid={`${testid}-box`}>
      {sel && !open ? (
        <button type="button" disabled={disabled} onClick={() => setOpen(true)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-left flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50" data-testid={testid} title={label(sel)}>
          <span className="truncate flex-1">{label(sel)}</span>
          {!disabled && <span role="button" onClick={pulisci} className="text-slate-400 hover:text-red-600" data-testid={`${testid}-pulisci`}><X className="h-4 w-4" /></span>}
        </button>
      ) : (
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-3 text-slate-400 pointer-events-none" />
          <Input autoFocus={open} disabled={disabled} value={q} placeholder={placeholder} className="pl-8" autoComplete="off"
            onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} data-testid={testid} />
          {loading && <Loader2 className="h-4 w-4 absolute right-2.5 top-3 text-slate-400 animate-spin" />}
        </div>
      )}
      {open && pos && createPortal(
        <div ref={lista} onPointerDown={(e) => e.stopPropagation()} style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, pointerEvents: "auto", maxHeight: Math.max(120, Math.min(320, window.innerHeight - pos.top - 12)) }} className="z-[100] rounded-md border border-slate-200 bg-white shadow-lg overflow-y-auto text-sm" data-testid={`${testid}-risultati`}>
          {q.trim().length < minChars ? (
            <div className="px-3 py-2 text-slate-400">Digita almeno {minChars} caratteri per cercare</div>
          ) : loading && ris.length === 0 ? (
            <div className="px-3 py-2 text-slate-400">Caricamento…</div>
          ) : err ? (
            <div className="px-3 py-2 text-red-600" data-testid={`${testid}-errore`}>{err}</div>
          ) : !loading && ris.length === 0 ? (
            <div className="px-3 py-2 text-slate-400">Nessun risultato</div>
          ) : ris.map((o) => (
            <div key={o.id} onMouseDown={(e) => { e.preventDefault(); scegli(o); }} className="px-3 py-1.5 cursor-pointer hover:bg-orange-50" data-testid={`${testid}-opzione-${o.id}`}>{label(o)}</div>
          ))}
        </div>, document.body)}
    </div>
  );
}

export const cercaFornitori = async (q) => (await api.get("/fornitori/cerca", { params: { q } })).data;
export const risolviFornitore = async (id) => (await api.get("/fornitori/cerca", { params: { ids: id } })).data[0] || null;

export function RicercaFornitore({ value, onChange, testid = "fornitore-search", placeholder = "Cerca fornitore…", className, disabled }) {
  return <RicercaAsync value={value} onChange={(o) => onChange(o ? o.id : "", o)} cerca={cercaFornitori} risolvi={risolviFornitore} label={(o) => o.ragione_sociale} placeholder={placeholder} testid={testid} className={className} disabled={disabled} />;
}

const SEP = "||";
const chiave = (a, v) => `${a.id}${SEP}${v || ""}`;
const espandi = (a) => ((a.varianti || []).length ? a.varianti : [""]).map((v) => ({ id: chiave(a, v), articolo_id: a.id, variante: v, codice: a.codice, descrizione: a.descrizione, um: a.um, peso_unitario: a.peso_unitario ?? null, um_peso: a.um_peso || "", unita_prezzo: a.unita_prezzo || "pz", label: `${a.codice} · ${a.descrizione}${v ? ` - ${v}` : ""}` }));
export const cercaArticoli = async (q) => (await api.get("/magazzino/articoli/cerca", { params: { q } })).data.flatMap(espandi);
export const risolviArticolo = async (key) => {
  const [id] = key.split(SEP);
  const a = (await api.get("/magazzino/articoli/cerca", { params: { ids: id } })).data[0];
  return a ? espandi(a).find((o) => o.id === key) || espandi(a)[0] : null;
};

// value: { articolo_id, variante } — onChange(opzione|null) con articolo_id, variante, um
export function RicercaArticolo({ articoloId, variante, onChange, onRisolto, testid = "articolo-search", placeholder = "Cerca articolo per codice, descrizione o dimensione…", className }) {
  const value = articoloId ? `${articoloId}${SEP}${variante || ""}` : "";
  return <RicercaAsync value={value} onChange={onChange} onRisolto={onRisolto} cerca={cercaArticoli} risolvi={risolviArticolo} label={(o) => o.label} placeholder={placeholder} testid={testid} className={className} />;
}
